import { IResponse, IResponsePagination } from '@/types/common';
import {
  IActivateKazokugotoPromptRequest,
  ICreateKazokugotoCharacterRequest,
  ICreateKazokugotoPromptRequest,
  IGenerateDailyKazokugotoRequest,
  IListKazokugotoItemsRequest,
  IUpdateKazokugotoCharacterRequest,
  IUpdateKazokugotoDuoRequest,
  IUpdateKazokugotoItemStatusRequest,
  IUpdateKazokugotoTopicRequest,
} from '@/types/request';
import {
  IKazokugotoCharacter,
  IKazokugotoDuo,
  IKazokugotoGenerationJob,
  IKazokugotoGenerationLogResponse,
  IKazokugotoItem,
  IKazokugotoPrompt,
  IKazokugotoTopic,
  IListPromptsResponse,
  IStartGenerationJobResponse,
} from '@/types/response';
import axiosClient from '../axiosClient';
import { ENDPOINT } from '../endpoint';

class Kazokugoto {
  /**
   * 一括生成ジョブを開始する（非同期）。202 で { jobId, status } が返る。
   * 完了は getGenerationJob でポーリングする。
   */
  startGeneration = (
    body: IGenerateDailyKazokugotoRequest,
  ): Promise<IResponse<IStartGenerationJobResponse>> => {
    return axiosClient.post(ENDPOINT.KAZOKUGOTO.GENERATE_DAILY, body);
  };

  /** 一括生成ジョブの状態取得（ポーリング用） */
  getGenerationJob = (
    jobId: number,
  ): Promise<IResponse<IKazokugotoGenerationJob>> => {
    return axiosClient.get(
      ENDPOINT.KAZOKUGOTO.GENERATION_JOB.replace(':id', String(jobId)),
    );
  };

  /** item 一覧（ページネーション + フィルタ） */
  listItems = (
    params: IListKazokugotoItemsRequest,
  ): Promise<IResponsePagination<IKazokugotoItem>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_ITEMS, { params });
  };

  /** item の生成時ログ（web 検索の参照リンク等）。ログ未保存の item は generationLog: null */
  getItemGenerationLog = (
    itemId: number,
  ): Promise<IResponse<IKazokugotoGenerationLogResponse>> => {
    return axiosClient.get(
      ENDPOINT.KAZOKUGOTO.GET_ITEM_GENERATION_LOG.replace(
        ':id',
        String(itemId),
      ),
    );
  };

  /** 既存 item のカバー画像のみ再生成 */
  regenerateCover = (
    itemId: number,
  ): Promise<IResponse<IKazokugotoItem>> => {
    return axiosClient.post(
      ENDPOINT.KAZOKUGOTO.REGENERATE_COVER.replace(':id', String(itemId)),
    );
  };

  /** item の公開ステータス変更 */
  updateItemStatus = (
    itemId: number,
    body: IUpdateKazokugotoItemStatusRequest,
  ): Promise<IResponse<IKazokugotoItem>> => {
    return axiosClient.patch(
      ENDPOINT.KAZOKUGOTO.UPDATE_ITEM_STATUS.replace(':id', String(itemId)),
      body,
    );
  };

  /** プロンプト ACTIVE 1 件ずつ一覧 */
  listActivePrompts = (): Promise<IResponse<IKazokugotoPrompt[]>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_PROMPTS, {
      params: { activeOnly: 1 },
    });
  };

  /** 全 key（distinct 名前のみ） */
  listAllPromptKeys = (): Promise<IResponse<IListPromptsResponse>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_PROMPTS);
  };

  /** key 単位で全 version（履歴） */
  getPromptVersions = (
    key: string,
  ): Promise<IResponse<IKazokugotoPrompt[]>> => {
    return axiosClient.get(
      ENDPOINT.KAZOKUGOTO.GET_PROMPT_BY_KEY.replace(':key', encodeURIComponent(key)),
    );
  };

  /** 新 version 作成（activate=true がデフォルト） */
  createPromptVersion = (
    body: ICreateKazokugotoPromptRequest,
  ): Promise<IResponse<IKazokugotoPrompt>> => {
    return axiosClient.post(ENDPOINT.KAZOKUGOTO.CREATE_PROMPT, body);
  };

  /** 既存 version を ACTIVE に切替 */
  activatePromptVersion = (
    key: string,
    body: IActivateKazokugotoPromptRequest,
  ): Promise<IResponse<IKazokugotoPrompt>> => {
    return axiosClient.put(
      ENDPOINT.KAZOKUGOTO.ACTIVATE_PROMPT.replace(':key', encodeURIComponent(key)),
      body,
    );
  };

  /** キャラクタ一覧（avatar URL + 出演曜日付き） */
  listCharacters = (): Promise<IResponse<IKazokugotoCharacter[]>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_CHARACTERS);
  };

  /** 新規キャラクタ作成（name + personality。avatar は作成後に差替） */
  createCharacter = (
    body: ICreateKazokugotoCharacterRequest,
  ): Promise<IResponse<IKazokugotoCharacter>> => {
    return axiosClient.post(ENDPOINT.KAZOKUGOTO.CREATE_CHARACTER, body);
  };

  /** personality 更新 */
  updateCharacter = (
    id: number,
    body: IUpdateKazokugotoCharacterRequest,
  ): Promise<IResponse<IKazokugotoCharacter>> => {
    return axiosClient.patch(
      ENDPOINT.KAZOKUGOTO.UPDATE_CHARACTER.replace(':id', String(id)),
      body,
    );
  };

  /** アバター画像差替（multipart） */
  uploadCharacterAvatar = (
    id: number,
    file: File,
  ): Promise<IResponse<IKazokugotoCharacter>> => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosClient.post(
      ENDPOINT.KAZOKUGOTO.UPLOAD_CHARACTER_AVATAR.replace(':id', String(id)),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  };

  /** topics 一覧（year / month 指定で絞り込み） */
  listTopics = (
    year?: number,
    month?: number,
  ): Promise<IResponse<IKazokugotoTopic[]>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_TOPICS, {
      params: {
        ...(year ? { year } : {}),
        ...(month ? { month } : {}),
      },
    });
  };

  /** topic 編集（theme / title / sub） */
  updateTopic = (
    id: number,
    body: IUpdateKazokugotoTopicRequest,
  ): Promise<IResponse<IKazokugotoTopic>> => {
    return axiosClient.patch(
      ENDPOINT.KAZOKUGOTO.UPDATE_TOPIC.replace(':id', String(id)),
      body,
    );
  };

  /** 曜日別コンビ一覧 */
  listDuos = (): Promise<IResponse<IKazokugotoDuo[]>> => {
    return axiosClient.get(ENDPOINT.KAZOKUGOTO.LIST_DUOS);
  };

  /** コンビの boke / tsukkomi 差し替え */
  updateDuo = (
    dayOfWeek: number,
    body: IUpdateKazokugotoDuoRequest,
  ): Promise<IResponse<IKazokugotoDuo>> => {
    return axiosClient.patch(
      ENDPOINT.KAZOKUGOTO.UPDATE_DUO.replace(':dayOfWeek', String(dayOfWeek)),
      body,
    );
  };
}

export const KazokugotoService = new Kazokugoto();
